import { visibleCategory, type ExpenseCategory, type Ingredient, type InvoiceLineFormat, type PurchaseUnit } from '@alexiarest/shared';
import { formatPurchaseFormat, formatQty, today } from '../format';
import { CATEGORY_GROUPS, categoryLabel } from '../labels';

/**
 * Bloque S · cómo habla el escáner único: fechas «28 oct» (nunca
 * «2026-09-28»), categorías «Materia prima › Comida» y presentaciones de
 * compra «Paquete de 8», «Pieza de 400 g».
 */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «2026-09-28» → «28 sep» (con el año solo si no es el de hoy). */
export function fechaCorta(iso: string | undefined, hoy: string = today()): string {
  const [y, m, d] = String(iso ?? '').split('-');
  const mes = MESES[Number(m) - 1];
  if (!y || !mes || !d) return String(iso ?? '');
  return `${Number(d)} ${mes}${y !== hoy.slice(0, 4) ? ` ${y}` : ''}`;
}

/** «Materia prima › Comida». */
export function etiquetaCategoria(category: ExpenseCategory): string {
  const visible = visibleCategory(category);
  const grupo = CATEGORY_GROUPS.find((g) => g.categories.includes(visible))?.label;
  return grupo ? `${grupo} › ${categoryLabel(visible)}` : categoryLabel(visible);
}

/** Las categorías por grupo, para el desplegable de «Corregir». */
export const GRUPOS_CATEGORIA = CATEGORY_GROUPS.map((g) => ({
  label: g.label,
  options: g.categories.map((c) => ({ value: c, label: categoryLabel(c) })),
}));

const mayuscula = (t: string): string => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

/** Cómo compras el ingrediente: «Paquete de 8», «Bolsa de 680 g», «1 kg». */
export function presentacionDeIngrediente(ing: Pick<Ingredient, 'presentation' | 'purchaseQty' | 'purchaseUnit'>): string {
  const p = ing.presentation?.trim();
  if (p && ing.purchaseUnit === 'pz' && ing.purchaseQty != null) return `${mayuscula(p)} de ${formatQty(ing.purchaseQty)}`;
  return mayuscula(formatPurchaseFormat(ing));
}

const PLURAL: Record<string, string> = {
  botella: 'botellas', bolsa: 'bolsas', frasco: 'frascos', bote: 'botes', caja: 'cajas', saco: 'sacos', lata: 'latas',
  garrafa: 'garrafas', garrafon: 'garrafones', paquete: 'paquetes', bulto: 'bultos', cubeta: 'cubetas', galon: 'galones',
  charola: 'charolas', costal: 'costales', tarro: 'tarros', bidon: 'bidones', pieza: 'piezas', manojo: 'manojos', rollo: 'rollos',
};
const ACENTO: Record<string, string> = { garrafon: 'garrafón', galon: 'galón', bidon: 'bidón' };

/** Presentación que saca Alexia del texto de la línea, para «Crearlo». */
export interface PresentacionLeida {
  /** «pieza de 400 g», «paquete de 6», «kg». */
  texto: string;
  /** «20 piezas de 400 g», «5 paquetes de 6», «10 kg». */
  compra: string;
  qty: number;
  unit: PurchaseUnit;
  /** Envase que se guarda como presentación del ingrediente. */
  envase?: string;
}

/**
 * De lo que dice la línea, la presentación de compra. Un tamaño de peso sin
 * envase escrito es una pieza («BARRA RUSTICA 400G» · 20 = 20 piezas de 400
 * g). Sin tamaño ni unidad no hay presentación: se pregunta «¿Cómo viene?».
 */
export function presentacionDeLinea(bought: InvoiceLineFormat | undefined, unit: PurchaseUnit | undefined, cantidad?: number): PresentacionLeida | undefined {
  const n = bought?.count ?? cantidad;
  if (bought?.packQty && bought.packUnit) {
    const envase = bought.envase ?? (bought.packUnit === 'g' || bought.packUnit === 'kg' ? 'pieza' : undefined);
    // 6440 g se lee mejor como 6.44 kg.
    const grande = bought.packQty >= 1000 && (bought.packUnit === 'g' || bought.packUnit === 'ml');
    // «paquete de 6 (90 g c/u)» (revisión del 14 oct 2026).
    const cu = bought.pieza ? ` (${formatQty(bought.pieza.qty, bought.pieza.unit)} c/u)` : '';
    const tam = bought.packUnit === 'pz' ? `${formatQty(bought.packQty)}${cu}`
      : grande ? formatQty(bought.packQty / 1000, bought.packUnit === 'g' ? 'kg' : 'l') : formatQty(bought.packQty, bought.packUnit);
    const singular = envase ? `${ACENTO[envase] ?? envase} de ${tam}` : tam;
    const compra = n && n !== 1 ? (envase ? `${formatQty(n)} ${PLURAL[envase] ?? envase} de ${tam}` : `${formatQty(n)} × ${tam}`) : singular;
    return { texto: singular, compra, qty: bought.packQty, unit: bought.packUnit, envase };
  }
  if (unit) return { texto: formatQty(1, unit), compra: n ? formatQty(n, unit) : formatQty(1, unit), qty: 1, unit };
  return undefined;
}
